import React, { useEffect, useState } from "react";
import { getConsent, setConsent } from "./cookieConsent";

// Cookie banner and preferences dialog. Reject is offered as prominently as
// Accept, and nothing optional runs until the visitor makes a choice.
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    // Show banner only if no consent stored yet
    const existing = getConsent();
    if (!existing) {
      setOpen(true);
    } else {
      // preload toggle state so Manage shows current prefs
      setAnalytics(!!existing.analytics);
    }

    // Allow reopening from anywhere (e.g. Cookie Policy or profile settings)
    const openSettings = () => {
      const latest = getConsent();
      setAnalytics(!!latest?.analytics);
      setShowManage(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-settings", openSettings);
    return () =>
      window.removeEventListener("open-cookie-settings", openSettings);
  }, []);

  const choose = (allowAnalytics) => {
    setConsent({ analytics: allowAnalytics });
    setAnalytics(allowAnalytics);
    setShowManage(false);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      style={styles.wrap}
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
    >
      <div style={styles.content}>
        {!showManage ? (
          <>
            <div style={styles.text}>
              We use essential cookies to keep you logged in and the site
              working. With your permission, we also use analytics cookies (our
              own and Google Analytics) to understand how the site is used so
              we can improve it. You can change your mind at any time. See our{" "}
              <a href="/cookies" style={styles.link}>
                Cookie Policy
              </a>{" "}
              and{" "}
              <a href="/privacy" style={styles.link}>
                Privacy Policy
              </a>
              .
            </div>
            <div style={styles.btnRow}>
              <button onClick={() => choose(false)} style={styles.btnPrimary}>
                Reject non-essential
              </button>
              <button
                onClick={() => setShowManage(true)}
                style={styles.btnGhost}
              >
                Manage preferences
              </button>
              <button onClick={() => choose(true)} style={styles.btnPrimary}>
                Accept all
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.text}>
              Choose what you’re happy with. <strong>Necessary</strong> cookies
              are always on to keep the site secure and working. Your choice is
              saved in this browser.
            </div>
            <div style={styles.toggles}>
              <Toggle
                label="Necessary"
                description="Keep you logged in and remember your settings, including this choice. Always on."
                checked
                disabled
              />
              <Toggle
                label="Analytics"
                description="Lets us recognise your browser on return visits (our own analytics) and use Google Analytics, so we can see which pages are useful. Never used for advertising."
                checked={analytics}
                onChange={setAnalytics}
              />
            </div>
            <div style={styles.btnRow}>
              <button
                onClick={() => setShowManage(false)}
                style={styles.btnGhost}
              >
                Back
              </button>
              <button onClick={() => choose(analytics)} style={styles.btnPrimary}>
                Save choices
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, description, checked, onChange, disabled }) {
  return (
    <label style={styles.toggleItem}>
      <span>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>{description}</div>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={{ width: 18, height: 18 }}
        aria-label={label}
      />
    </label>
  );
}

const styles = {
  wrap: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    padding: 12,
    background: "rgba(17,17,17,0.9)",
    backdropFilter: "saturate(140%) blur(6px)",
  },
  content: {
    margin: "0 auto",
    maxWidth: 960,
    background: "#111",
    color: "#fff",
    borderRadius: 12,
    padding: 16,
    border: "1px solid rgba(255,255,255,0.12)",
  },
  text: { fontSize: 14, lineHeight: 1.5, marginBottom: 12 },
  link: { color: "#a5b4fc", textDecoration: "underline" },
  btnRow: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  btnPrimary: {
    background: "#fff",
    color: "#111",
    border: "1px solid #fff",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  },
  btnGhost: {
    background: "transparent",
    color: "#fff",
    border: "1px dashed rgba(255,255,255,0.4)",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
  },
  toggles: { display: "grid", gap: 10, margin: "12px 0 8px" },
  toggleItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(255,255,255,0.06)",
  },
};
